import { skipToken } from "@reduxjs/toolkit/query";
import { reconcileCourse } from "@repo/app-types/entities/course";
import { buildSectionDefinitionsById } from "@repo/app-types/entities/interviewSection/definition";
import type { JobType } from "@repo/app-types/entities/jobType";
import { useMemo } from "react";
import { useAllInterviewSectionDefinitionsForJobTypeQuery } from "../../../../api/entities/interviewSectionDefinition.js";

/** Fetches the job type's interview sections and returns the reconciled course tree. */
export function useCourseForJobType(jobType: JobType) {
  const { sections, sectionsById, isLoading, isError } = useSectionsForJobType(
    jobType?.id,
  );

  // Dynamically reconcile any inconsistencies between the course tree and the actual interview section definitions. This will be persisted when the user saves the course.
  const courseFromJobType = jobType.settings.course;
  const course = useMemo(
    () => (sections ? reconcileCourse(courseFromJobType, sections) : undefined),
    [courseFromJobType, sections],
  );

  return {
    course,
    sections,
    sectionsById,
    isLoading,
    isError,
  };
}

/** Fetches the interview section definitions for a specific job type. */
export function useSectionsForJobType(jobTypeId: string | undefined) {
  const {
    data: sections,
    isLoading,
    isError,
  } = useAllInterviewSectionDefinitionsForJobTypeQuery(
    jobTypeId !== undefined ? { jobTypeId } : skipToken,
  );

  const sectionsById = useMemo(
    () => (sections ? buildSectionDefinitionsById(sections) : undefined),
    [sections],
  );

  return { sections, sectionsById, isLoading, isError };
}
